import { Images } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useParams } from "react-router";
import { PageFrame } from "@/components/common";
import { resolveCatalogTranslation } from "@/lib/translationResolution";
import {
  carouselSlideKeys,
  carouselSlideService,
} from "@/services/carouselSlideService";
import { Permission } from "@/types";
import { useAuthStore } from "@/store/authStore";
import CarouselSlideForm from "./CarouselSlideForm";
import { CAROUSEL_LIST_PATH } from "./carouselSlideFormModel";

export default function CarouselSlideEditPage() {
  const { t, i18n } = useTranslation("adminCarousel");
  const navigate = useNavigate();
  const { slideId } = useParams<{ slideId: string }>();
  const canWrite = useAuthStore((s) =>
    s.hasPermission(Permission.CAROUSEL_SLIDES_WRITE),
  );

  const {
    data: slide,
    isLoading,
    error,
    refetch,
  } = useQuery({
    queryKey: carouselSlideKeys.detail(slideId ?? ""),
    queryFn: () => {
      if (!slideId) throw new Error("Missing slide id");
      return carouselSlideService.get(slideId);
    },
    enabled: Boolean(slideId),
  });

  if (!slideId) {
    return (
      <PageFrame
        title={t("form.editTitle")}
        icon={Images}
        error
        errorMessage={t("errors.loadError")}
        onRetry={() => navigate(CAROUSEL_LIST_PATH)}
        backLink={{ fallback: CAROUSEL_LIST_PATH, children: t("title") }}
      />
    );
  }

  return (
    <PageFrame
      title={t("form.editTitle")}
      description={
        slide
          ? resolveCatalogTranslation(slide.title_translations, i18n.language)
          : undefined
      }
      icon={Images}
      isLoading={isLoading}
      error={Boolean(error)}
      errorMessage={t("errors.loadError")}
      onRetry={() => void refetch()}
      backLink={{ fallback: CAROUSEL_LIST_PATH, children: t("title") }}
    >
      {slide ? (
        <CarouselSlideForm
          key={slide.id}
          initial={slide}
          regionId={slide.region_id}
          canWrite={canWrite}
        />
      ) : null}
    </PageFrame>
  );
}
